import './variant-picker.js';

export const lines = (value = '') => value.split(/\r?\n/).map(line => line.split('|').map(part => part.trim())).filter(parts => parts[0]);
export const key = (value = '') => value.normalize('NFKC').trim().toLocaleLowerCase();
export const names = (value = '') => value.split(',').map(key).filter(Boolean);
export function configuration(settings, records = []) {
  const groups = lines(settings.groups).map(([id, title, visible]) => ({ id, title, visible: key(visible) !== 'hide' }));
  const rules = lines(settings.values).map(([option, value, group = '', color = '', image = '', visible = 'show'], order) => ({ option, value, group, color, image, visible: key(visible) !== 'hide', order }));
  records.forEach((record, index) => {
    const match = rules.find(rule => key(rule.option) === key(record.option) && key(rule.value) === key(record.value));
    const data = { ...record, visible: record.visible !== 'false', order: rules.length + index };
    if (match) Object.assign(match, data, { order: match.order }); else rules.push(data);
  });
  return { groups, rules };
}
export function ruleFor(rules, option, value, scent) {
  return rules.find(rule => key(rule.option) === key(option) && key(rule.value) === key(value))
    ?? rules.find(rule => (rule.option === '*' || scent && names('Scent,Fragrance,Flavor,Flavour').includes(key(rule.option))) && key(rule.value) === key(value));
}
export class EdenOptions extends HTMLElement {
  connectedCallback() {
    if (this.observer) return;
    this.controller = new AbortController();
    this.observer = new MutationObserver(() => this.decorate());
    this.onChange = () => this.updateLabels();
    this.addEventListener('change', this.onChange, { signal: this.controller.signal });
    this.addEventListener('focusin', event => { this.focusValue = event.target.dataset?.optionValueId; }, { signal: this.controller.signal });
    this.scope = this.closest('.shopify-section') || this.parentElement;
    this.scope?.addEventListener('submit', event => {
      if (!this.empty || !event.target.closest('product-form-component')) return;
      event.preventDefault(); event.stopImmediatePropagation();
    }, { capture: true, signal: this.controller.signal });
    this.decorate();
  }
  disconnectedCallback() {
    this.observer?.disconnect(); this.observer = null;
    this.controller?.abort();
  }
  decorate() {
    this.observer?.disconnect();
    const source = this.querySelector('[data-eden-settings]');
    if (!source) return;
    let settings;
    try { settings = JSON.parse(source.textContent); } catch { return; }
    const records = [...this.querySelectorAll('[data-eden-record]')].map(node => ({ ...node.dataset, option: node.dataset.option, value: node.dataset.value }));
    const config = configuration(settings, records);
    const picker = this.querySelector('variant-picker');
    let selectionChanged = false;
    let changeTarget;
    this.empty = false;
    for (const fieldset of picker?.querySelectorAll('fieldset[data-option-name]') || []) {
      if (fieldset.dataset.edenReady === 'true') continue;
      const option = fieldset.dataset.optionName;
      const scent = names(settings.scent_options || 'Scent,Fragrance').includes(key(option));
      const size = names(settings.size_options).includes(key(option));
      const override = lines(settings.option_modes).find(([name]) => key(name) === key(option));
      const mode = key(override?.[1] || (scent ? 'swatches' : size ? 'buttons' : settings.other_mode || 'buttons'));
      fieldset.dataset.edenMode = ['buttons', 'swatches', 'dropdown'].includes(mode) ? mode : 'buttons';
       fieldset.classList.remove('variant-option--buttons', 'variant-option--swatches', 'variant-option--equal-width-buttons', 'variant-option--auto-width-buttons');
      fieldset.dataset.edenReady = 'true';
      const legend = fieldset.querySelector('legend');
      legend?.classList.add('eden-option-heading');
      if (scent && !settings.show_scent_heading) legend?.classList.add('eden-visually-hidden');
      if (legend) legend.textContent = option;
      const items = [...fieldset.querySelectorAll('label.variant-option__button-label')].map((label, index) => {
        const input = label.querySelector('input');
        const rule = ruleFor(config.rules, option, input.value, scent);
        const group = scent ? config.groups.find(group => group.id === rule?.group) : null;
        const visible = rule?.visible !== false && (scent ? group ? group.visible : settings.fallback_visible !== false : true);
        const nativeSwatch = label.querySelector('.swatch');
        const nativeImage = nativeSwatch?.querySelector('img');
        let image = rule?.image || nativeImage?.src;
        let color = rule?.color;
        if (!color && nativeSwatch) color = getComputedStyle(nativeSwatch).getPropertyValue('--swatch-background').trim();
        label.className = 'eden-choice';
        label.hidden = !visible;
        input.disabled = !visible;
        input.removeAttribute('aria-disabled');
        input.setAttribute('aria-label', input.value);
        const visual = document.createElement('span'); visual.className = 'eden-visual'; visual.setAttribute('aria-hidden', 'true');
        if (mode === 'swatches') {
          visual.style.background = color || settings.fallback_color;
          if (image) {
            try {
              const url = new URL(image, location.origin);
              if (url.protocol === 'https:' || url.protocol === 'http:') {
                const img = document.createElement('img'); img.src = url.href; img.alt = ''; img.width = 64; img.height = 64; img.loading = 'lazy'; visual.append(img);
              }
            } catch {}
          } else if (nativeSwatch && !rule?.color) {
            visual.append(nativeSwatch.cloneNode(true));
          }
        } else visual.textContent = input.value;
        const text = document.createElement('span'); text.className = 'eden-value-label'; text.textContent = input.value;
        label.replaceChildren(input, visual, text);
        label.dataset.available = input.dataset.optionAvailable;
        return { label, input, rule, group: group?.id ?? '__fallback', visible, index };
      });
      const visible = items.filter(item => item.visible);
      const checkedHidden = items.some(item => item.input.checked && !item.visible);
      if (checkedHidden || !items.some(item => item.input.checked)) {
        items.forEach(item => { item.input.checked = false; item.input.dataset.currentChecked = 'false'; });
        if (visible[0]) {
          visible[0].input.checked = true; visible[0].input.dataset.currentChecked = 'true';
          changeTarget = visible[0].input;
        }
        selectionChanged = true;
      }
      fieldset.dataset.edenEmpty = String(!visible.length);
      const sorted = [...items].sort((a, b) => (a.rule?.order ?? Infinity) - (b.rule?.order ?? Infinity) || a.index - b.index);
      if (fieldset.dataset.edenMode === 'dropdown') {
        const select = document.createElement('select'); select.className = 'eden-select'; select.setAttribute('aria-label', option);
        for (const item of sorted) {
          item.label.hidden = true;
          item.input.checked = false;
          if (!item.visible) continue;
          const opt = document.createElement('option'); opt.value = item.input.value; opt.textContent = item.input.value;
          Object.assign(opt.dataset, item.input.dataset);
          if (item.input.dataset.currentChecked === 'true') { opt.selected = true; opt.setAttribute('selected', 'selected'); }
          select.append(opt);
        }
        if (!select.querySelector('option[selected]') && select.options[0]) select.options[0].setAttribute('selected', 'selected');
        select.disabled = !visible.length;
        fieldset.append(select);
        if (changeTarget && fieldset.contains(changeTarget)) changeTarget = select;
      } else {
        const groups = scent ? [...config.groups, { id: '__fallback', title: settings.fallback_heading, visible: settings.fallback_visible !== false }] : [{ id: '__fallback', title: '', visible: true }];
        for (const group of groups) {
          const members = sorted.filter(item => item.visible && item.group === group.id);
          if (!members.length || !group.visible) continue;
          const section = document.createElement('div'); section.className = 'eden-group';
          if (scent && group.title) { const heading = document.createElement('div'); heading.className = 'eden-group-heading'; heading.textContent = group.title; section.append(heading); }
          const row = document.createElement('div'); row.className = 'eden-row'; members.forEach(item => row.append(item.label)); section.append(row); fieldset.append(section);
        }
      }
      const selected = document.createElement('div'); selected.className = 'eden-selected-name'; selected.setAttribute('aria-live', 'polite'); fieldset.append(selected);
    }
    this.empty = !!picker?.querySelector('[data-eden-empty="true"]');
    this.updateLabels();
    const message = this.querySelector('[data-eden-message]');
    if (message) { message.hidden = !this.empty; message.textContent = settings.empty_text || ''; }
    this.toggleAttribute('data-eden-empty', this.empty);
    this.syncPurchaseGuard();
    this.observer?.observe(this, { childList: true, subtree: true });
    if (selectionChanged && changeTarget && !this.empty) queueMicrotask(() => { if (changeTarget.isConnected) changeTarget.dispatchEvent(new Event('change', { bubbles: true })); });
    if (this.focusValue && document.activeElement === document.body) {
      [...this.querySelectorAll('input[data-option-value-id]')].find(input => input.dataset.optionValueId === this.focusValue && !input.disabled)?.focus({ preventScroll: true });
    }
  }
  syncPurchaseGuard() {
    for (const form of this.scope?.querySelectorAll('product-form-component') || []) {
      if (form.dataset.productId !== this.querySelector('variant-picker')?.dataset.productId) continue;
      if (this.empty) {
        const id = form.querySelector('input[name="id"]');
        if (id?.value) { id.dataset.edenPreviousValue = id.value; id.value = ''; }
        form.querySelectorAll('button[type="submit"], input[name="id"]').forEach(node => { if (!node.disabled) { node.dataset.edenDisabled = 'true'; node.disabled = true; } });
        form.querySelectorAll('shopify-accelerated-checkout, [ref="acceleratedCheckoutButtonContainer"]').forEach(node => { node.dataset.edenHidden = 'true'; node.hidden = true; });
      } else {
        const id = form.querySelector('input[name="id"]');
        if (id?.dataset.edenPreviousValue && !id.value) { id.value = id.dataset.edenPreviousValue; delete id.dataset.edenPreviousValue; }
        form.querySelectorAll('[data-eden-disabled]').forEach(node => { node.disabled = false; delete node.dataset.edenDisabled; });
        form.querySelectorAll('[data-eden-hidden]').forEach(node => { node.hidden = false; delete node.dataset.edenHidden; });
      }
    }
  }
  updateLabels() {
    for (const fieldset of this.querySelectorAll('fieldset[data-eden-ready]')) {
      const selected = fieldset.querySelector('input:checked')?.value || fieldset.querySelector('select')?.value || '';
      const node = fieldset.querySelector('.eden-selected-name');
      if (node) node.textContent = selected;
    }
  }
}
if (!customElements.get('eden-options')) customElements.define('eden-options', EdenOptions);
